// ═══════════════════════════════════════════════════════════════
// BOOKSHELF
// ═══════════════════════════════════════════════════════════════

import supabase from "../services/supabaseClient";
import { getUnityBridgeCommands } from "./unityCommandBridge";

// ═══════════════════════════════════════════════════════════════
// 1. CATEGORIES, COLOURS AND SEARCH
// ═══════════════════════════════════════════════════════════════

export const CATEGORIES = [
  { id: "document",   label: "Document",   color: "#F5A623", types: ["document"] },
  { id: "note",       label: "Note",       color: "#4DB8B5", types: ["note"] },
  { id: "reference",  label: "Reference",  color: "#9B8FE0", types: ["reference"] },
  { id: "whiteboard", label: "Whiteboard", color: "#E5484D", types: ["whiteboard-cards", "whiteboard-canvas"] },
  { id: "upload",     label: "Upload",     color: "#3B9EFF", types: ["upload"] },
];

const TYPE_TO_CATEGORY = CATEGORIES.reduce((acc, cat) => {
  cat.types.forEach((t) => { acc[t] = cat.id; });
  return acc;
}, {});

export function categoryForType(type) {
  return TYPE_TO_CATEGORY[type] || null;
}

export function categoryById(id) {
  return CATEGORIES.find((c) => c.id === id) || null;
}

export function colorForType(type) {
  const cat = categoryById(categoryForType(type));
  return cat ? cat.color : "#A0A0A0";
}

export function labelForType(type) {
  const cat = categoryById(categoryForType(type));
  return cat ? cat.label : "Document";
}

const HTML_TAG = /<\/?(p|div|h[1-6]|ul|ol|li|br|hr|strong|b|em|i|u|s|a|span|blockquote|pre|code|img|table|thead|tbody|tr|td|th)\b[^>]*>/i;

export function toPlainText(value) {
  if (typeof value !== "string" || !value) return "";
  if (!HTML_TAG.test(value)) return value;
  const withBreaks = value
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(p|div|h[1-6]|li|blockquote|tr|pre)\s*>/gi, "\n");
  const parsed = new DOMParser().parseFromString(withBreaks, "text/html");
  return (parsed.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}

const UNSAFE_TAGS = ["script", "style", "iframe", "object", "embed", "link", "meta", "form"];

export function toSafeHtml(value) {
  if (typeof value !== "string" || !value) return "";
  const doc = new DOMParser().parseFromString(value, "text/html");

  doc.body.querySelectorAll(UNSAFE_TAGS.join(",")).forEach((el) => el.remove());
  doc.body.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const val = attr.value.replace(/\s+/g, "").toLowerCase();

      if (name.startsWith("on") || val.startsWith("javascript:") || val.startsWith("data:text/html")) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return doc.body.innerHTML;
}

export function scoreMatch(doc, query) {
  const words = String(query || "").toLowerCase().split(/\s+/).filter((w) => w.length >= 2);
  if (!words.length) return 0;

  const haystack = [
    doc?.title || "",
    typeof doc?.content === "string" ? toPlainText(doc.content) : "",
    (doc?.tags || []).join(" "),
    doc?.fileName || "",
  ].join(" ").toLowerCase();

  const hits = words.filter((w) => haystack.includes(w)).length;
  return hits / words.length;
}

export function filterDocuments(docs, { categories = [], query = "" } = {}) {
  let result = docs || [];

  if (categories.length) {
    const allowed = new Set(
      categories.flatMap((id) => categoryById(id)?.types || [])
    );
    result = result.filter((d) => allowed.has(d.type));
  }

  if (String(query || "").trim()) {
    result = result.filter((d) => scoreMatch(d, query) > 0);
  }

  return result;
}

export function buildMatchMap(docs, query) {
  const map = {};
  if (!String(query || "").trim()) return map;
  (docs || []).forEach((d) => {
    const score = scoreMatch(d, query);
    if (score > 0) map[d.id] = score;
  });
  return map;
}

// ═══════════════════════════════════════════════════════════════
// 2. BOOK <-> DESK DOCUMENT IDS
// ═══════════════════════════════════════════════════════════════

/** Book ids for documents that were first written at a hotspot. */
export const DESK_BOOK_PREFIX = "desk-";
/** Desk doc ids for books that were first created on the shelf (an upload, say). */
export const BOOK_DESK_PREFIX = "bk-";

/** The book id for a document currently sitting on the Desk. */
export function bookIdForDeskDoc(deskDocId) {
  if (!deskDocId) return null;
  const id = String(deskDocId);
  return id.startsWith(BOOK_DESK_PREFIX)
    ? id.slice(BOOK_DESK_PREFIX.length)
    : `${DESK_BOOK_PREFIX}${id}`;
}

/** The Desk doc id to use when a book is opened at the Desk. */
export function deskDocIdForBook(bookId) {
  if (!bookId) return null;
  const id = String(bookId);
  return id.startsWith(DESK_BOOK_PREFIX)
    ? id.slice(DESK_BOOK_PREFIX.length)
    : `${BOOK_DESK_PREFIX}${id}`;
}

// ═══════════════════════════════════════════════════════════════
// 3. SUPABASE: bookshelf_documents
// ═══════════════════════════════════════════════════════════════

export const BOOKSHELF_TABLE = "bookshelf_documents";

function rowToDoc(row) {
  return {
    id: row.id,
    title: row.title,
    type: row.type,
    priorType: row.prior_type,
    slot: row.slot,
    content: row.content ?? "",
    sourceHotspot: row.source_hotspot,
    mimeType: row.mime_type,
    fileName: row.file_name,
    fileSize: row.file_size,
    storagePath: row.storage_path,
    roomId: row.room_id,
    status: row.status,
    tags: row.tags || [],
    createdAt: new Date(row.created_at).getTime(),
    updatedAt: new Date(row.updated_at).getTime(),
    lastOpenedBy: row.last_opened_by,
  };
}

function docToRow(userId, doc) {
  return {
    id: doc.id,
    user_id: userId,
    room_id: doc.roomId ?? null,
    title: doc.title,
    type: doc.type,
    prior_type: doc.priorType ?? null,
    slot: Number.isInteger(doc.slot) ? doc.slot : null,
    content: typeof doc.content === "string" ? doc.content : (doc.content == null ? "" : JSON.stringify(doc.content)),
    source_hotspot: doc.sourceHotspot ?? null,
    mime_type: doc.mimeType ?? null,
    file_name: doc.fileName ?? null,
    file_size: doc.fileSize ?? null,
    storage_path: doc.storagePath ?? null,
    status: doc.status,
    tags: doc.tags || [],
    last_opened_by: doc.lastOpenedBy ?? null,
    created_at: new Date(doc.createdAt || Date.now()).toISOString(),
    updated_at: new Date(doc.updatedAt || Date.now()).toISOString(),
  };
}

export async function listBookshelfDocuments(userId) {
  if (!userId) return [];
  const { data, error } = await supabase
    .from(BOOKSHELF_TABLE)
    .select("*")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(rowToDoc);
}

export async function upsertBookshelfDocument(userId, doc) {
  if (!userId) throw new Error("upsertBookshelfDocument requires userId");
  const { error } = await supabase
    .from(BOOKSHELF_TABLE)
    .upsert(docToRow(userId, doc), { onConflict: "user_id,id" });
  if (error) throw error;
}

export async function deleteBookshelfDocument(userId, id) {
  if (!userId) return;
  const { error } = await supabase
    .from(BOOKSHELF_TABLE)
    .delete()
    .eq("user_id", userId)
    .eq("id", id);
  if (error) throw error;
}

// ═══════════════════════════════════════════════════════════════
// 4. STARTER-BOOK SEEDING MARKER
// ═══════════════════════════════════════════════════════════════

const SEEDED_KEY = (userId, roomId) =>
  `nq_bookshelf_seeded_${userId || "anon"}_${roomId || "none"}`;

export function hasSeededRoomBookshelf(userId, roomId) {
  try {
    return localStorage.getItem(SEEDED_KEY(userId, roomId)) === "1";
  } catch {
    return false;
  }
}

export function markRoomBookshelfSeeded(userId, roomId) {
  try {
    localStorage.setItem(SEEDED_KEY(userId, roomId), "1");
  } catch {
  }
}

// ═══════════════════════════════════════════════════════════════
// 5. BOOKSHELF -> UNITY
// ═══════════════════════════════════════════════════════════════

export function toUnityType(type) {
  return categoryForType(type) || "document";
}

export function toUnityBook(doc) {
  return {
    id: doc.id,
    title: doc.title || "Untitled",
    type: toUnityType(doc.type),
    is_master: Boolean(doc.isMaster),
  };
}

export function sendHighlightBooks(entries) {
  if (process.env.NODE_ENV === "development") {
    console.info("[React → Unity] HighlightBooks", JSON.stringify(entries || []));
  }
  getUnityBridgeCommands()?.highlightBooks?.(entries || []);
}

const syncedBookIds = new Set();

export function markBooksSynced(ids) {
  (ids || []).forEach((id) => syncedBookIds.add(id));
}

export function unsyncedBooks(docs) {
  return (docs || []).filter((d) => d?.id && !syncedBookIds.has(d.id));
}

export function sendAddBook(doc) {
  if (process.env.NODE_ENV === "development") {
    console.info("[React → Unity] AddBook", toUnityBook(doc));
  }
  syncedBookIds.add(doc.id);
  getUnityBridgeCommands()?.addBook?.(toUnityBook(doc));
}

export function sendRemoveBook(bookId) {
  if (process.env.NODE_ENV === "development") {
    console.info("[React → Unity] RemoveBook", bookId);
  }
  syncedBookIds.delete(bookId);
  getUnityBridgeCommands()?.removeBook?.(bookId);
}

export function sendBookEditMode(enabled) {
  if (process.env.NODE_ENV === "development") {
    console.info("[React → Unity] SetBookEditMode", Boolean(enabled));
  }
  getUnityBridgeCommands()?.setBookEditMode?.(Boolean(enabled));
}

export function sendOpenBooks(bookIds) {
  if (process.env.NODE_ENV === "development") {
    console.info("[React → Unity] SetOpenBooks", JSON.stringify(bookIds || []));
  }
  getUnityBridgeCommands()?.setOpenBooks?.(bookIds || []);
}
